/**
 * Whether amounts are shown with their currency code, on this device.
 *
 * A display preference, not a fact about the catalog. Every amount already
 * carries its own currency and always will -- `NFR-FUNC-3` requires it, so
 * "None" cannot mean an amount with no currency. It means the code is not
 * printed beside the number, which is what someone with a single-currency
 * catalog wants: "89.00" says everything "89.00 USD" does and says it shorter.
 *
 * Kept on the device, like the theme and for a weaker version of the same
 * reason: it changes how a number is drawn, not what it is. Losing it costs a
 * preference and no data, and two devices disagreeing produces two renderings of
 * one identical amount rather than two amounts.
 *
 * USD is the only code offered for now. The list is a list rather than a boolean
 * because the next entry is a currency and not a second kind of yes.
 */
export type CurrencyPreference = 'none' | 'USD';

/** The choices, in the order they are offered. */
export const CURRENCIES: readonly { value: CurrencyPreference; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'USD', label: 'USD — US dollar' },
];

const KEY = 'intentionalist.currency';

const isPreference = (value: string | null): value is CurrencyPreference =>
  value === 'none' || value === 'USD';

/**
 * What this device has chosen, or `none`.
 *
 * Read on every format rather than cached, because the alternative is a cache to
 * invalidate when the setting changes and a screen that keeps printing the old
 * answer until it is reloaded. `localStorage` reads are synchronous and this is
 * a few per render.
 */
export function currencyDisplay(): CurrencyPreference {
  try {
    const stored = localStorage.getItem(KEY);
    return isPreference(stored) ? stored : 'none';
  } catch {
    // Storage denied, or private browsing. Bare amounts are the safe default:
    // they are never wrong, only less specific.
    return 'none';
  }
}

/** Records the choice on this device. */
export function setCurrencyDisplay(preference: CurrencyPreference): void {
  try {
    localStorage.setItem(KEY, preference);
  } catch {
    // The choice holds for this session and is forgotten on reload, which is
    // the same way the device id degrades.
  }
}

/** Testing seam only. */
export function resetCurrencyDisplay(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing depends on this having worked.
  }
}

/**
 * What to put after a money field's label, so entry states the assumption.
 *
 * FR-2.18 makes the setting govern what an entered amount *is*, not only how a
 * stored one reads, and the field is the only place that claim is actionable: a
 * price typed off a receipt in another currency is wrong the moment it is saved,
 * and nothing downstream can tell it apart from a correct one. Empty when no
 * currency is set, which leaves every existing label exactly as it was.
 */
export function currencySuffix(): string {
  const preference = currencyDisplay();
  return preference === 'none' ? '' : ' (' + preference + ')';
}
